import React, { useState } from "react";
import {
  Play,
  Calendar,
  Activity,
  Zap,
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  Mic,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { CLIENT_DEMO } from "../data/clientDemo";
import WeekModal from "../components/WeekModal";

// --- COMPONENT: Timeline ---
const PhaseTimeline = ({ steps, activeWeek, onWeekSelect }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "32px",
      overflowX: "auto",
      paddingBottom: "4px",
    }}
  >
    {steps.map((s, i) => (
      <div
        key={i}
        onClick={() => onWeekSelect(s.week)}
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          opacity: s.week === activeWeek ? 1 : 0.5,
          transition: "opacity 0.2s",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: s.week === activeWeek ? "var(--brand)" : "var(--muted)",
            display: "flex",
            flexDirection: "column",
            gap: "2px",
          }}
        >
          <span style={{ fontSize: "9px", opacity: 0.7 }}>WEEK {s.week}</span>
          {s.label}
        </div>
        {i !== steps.length - 1 && (
          <ChevronRight
            size={14}
            color="var(--line)"
            style={{ margin: "0 16px" }}
          />
        )}
      </div>
    ))}
  </div>
);

// --- COMPONENT: Signal Bar ---
const SignalBar = ({ label, status, level, color }) => {
  const blocks = Array.from({ length: 10 }, (_, i) => i + 1);
  return (
    <div style={{ marginBottom: "16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "6px",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "1px",
          textTransform: "uppercase",
        }}
      >
        <span style={{ color: "var(--muted)" }}>{label}</span>
        <span style={{ color: color }}>{status}</span>
      </div>
      <div style={{ display: "flex", gap: "4px", height: "6px" }}>
        {blocks.map((b) => (
          <div
            key={b}
            style={{
              flex: 1,
              borderRadius: "2px",
              background: level >= b ? color : "rgba(255,255,255,0.05)",
              opacity: level >= b ? 1 : 0.3,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default function ClientView() {
  const [selectedWeek, setSelectedWeek] = useState(CLIENT_DEMO.currentWeek);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Data slicing
  const client = CLIENT_DEMO.client;
  const currentWeekData = CLIENT_DEMO.weeks[selectedWeek];
  const ui = CLIENT_DEMO.ui;

  // Mode Logic
  const cta = currentWeekData.cta || { label: "View Details", mode: "VIEW" };

  // --- HANDLER: OPEN MODAL ---
  const handleCtaClick = () => {
    // 1. If there is explicit preview/modal data defined in JSON (Future Weeks), use it
    if (
      currentWeekData.preview &&
      currentWeekData.preview.enabled &&
      currentWeekData.preview.modal
    ) {
      setModalData(currentWeekData.preview.modal);
    }
    // 2. Otherwise, auto-generate modal data from the current week's plan (Past/Current Weeks)
    else {
      setModalData({
        title: `${currentWeekData.coachNote.title}`,
        subtitle: currentWeekData.focus.title,
        why: currentWeekData.coachNote.summary,
        stays: ["Core System", "Focus"],
        changes: [currentWeekData.plan.adjustment],
        checklist: currentWeekData.plan.tasks.map((t) => t.title),
        coachLine: "Progress is alignment.",
      });
    }
    setModalOpen(true);
  };

  return (
    <div className="container" style={{ paddingBottom: 80 }}>
      {/* 1. HEADER */}
      <div
        className="secHead"
        style={{ alignItems: "center", marginBottom: "24px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              background: "var(--surface)",
              border: "1px solid var(--line)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "700",
              color: "var(--brand)",
            }}
          >
            {client.initials}
          </div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: "700", color: "#fff" }}>
              Welcome back, {client.name}
            </div>
            <div style={{ fontSize: "13px", color: "var(--muted)" }}>
              System Status:{" "}
              <span style={{ color: "var(--brand)" }}>
                {client.systemStatus}
              </span>
            </div>
          </div>
        </div>
        <button
          className="btn secondary"
          style={{ fontSize: "12px", height: "36px" }}
        >
          {ui.cta.logCheckIn}
        </button>
      </div>

      {/* 2. TIMELINE */}
      <PhaseTimeline
        steps={CLIENT_DEMO.timeline}
        activeWeek={selectedWeek}
        onWeekSelect={setSelectedWeek}
      />

      {/* 3. COACH NOTE */}
      <section style={{ marginBottom: "32px" }}>
        <div
          style={{
            marginBottom: "12px",
            fontSize: "14px",
            color: "var(--muted)",
            fontStyle: "italic",
          }}
        >
          "{ui.contextLines[selectedWeek]}"
        </div>

        <div
          className="glass"
          style={{
            padding: "0",
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "0",
          }}
        >
          {/* Video Player */}
          <div
            style={{
              height: "240px",
              background: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.8)), url(${currentWeekData.coachNote.videoThumbnail})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                transition: "transform 0.2s",
              }}
            >
              <Play
                size={24}
                fill="#fff"
                color="#fff"
                style={{ marginLeft: "4px" }}
              />
            </div>
            <div
              style={{
                position: "absolute",
                bottom: "16px",
                left: "16px",
                background: "#000",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "700",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Mic size={12} color="var(--brand)" /> Coach's Note
            </div>
            <div
              style={{
                position: "absolute",
                bottom: "16px",
                right: "16px",
                background: "rgba(0,0,0,0.6)",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "700",
              }}
            >
              {currentWeekData.coachNote.duration}
            </div>
          </div>

          {/* Note Summary */}
          <div
            style={{
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#fff",
                marginBottom: "8px",
              }}
            >
              "{currentWeekData.coachNote.title}"
            </div>
            <p
              style={{
                margin: "0 0 20px 0",
                fontSize: "15px",
                lineHeight: "1.6",
                color: "var(--muted)",
              }}
            >
              {currentWeekData.coachNote.summary}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                fontSize: "12px",
                color: "var(--muted)",
              }}
            >
              <MessageSquare size={14} /> Posted{" "}
              {currentWeekData.coachNote.posted}
            </div>
          </div>
        </div>
      </section>

      {/* 4. DASHBOARD GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
        }}
      >
        {/* Signals */}
        <div
          className="glass"
          style={{ padding: "32px", display: "flex", flexDirection: "column" }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "800",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "24px",
            }}
          >
            <Activity size={16} color="var(--brand)" /> System Signals
          </h3>
          <div style={{ flex: 1 }}>
            {currentWeekData.signals.map((s, i) => (
              <SignalBar key={i} {...s} />
            ))}
          </div>
        </div>

        {/* Pivot & Plan */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Primary Focus */}
          <div
            className="glass"
            style={{
              padding: "24px",
              borderLeft: "4px solid #f59e0b",
              background:
                "linear-gradient(90deg, rgba(245, 158, 11, 0.05) 0%, rgba(15, 23, 42, 0.4) 100%)",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "800",
                color: "#f59e0b",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={14} /> Primary Focus
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "#fff",
                marginBottom: "8px",
              }}
            >
              {currentWeekData.focus.title}
            </div>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                color: "var(--muted)",
                opacity: 0.8,
              }}
            >
              {currentWeekData.focus.subtitle}
            </p>
          </div>

          {/* Plan Adjustment & CTA */}
          <div
            className="glass highlight"
            style={{
              padding: "24px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "var(--brand)",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Zap size={14} /> Week {selectedWeek} Plan
              </div>
              {/* Show "ADJUSTED" only if we are in the Current Active Week */}
              {selectedWeek === CLIENT_DEMO.currentWeek && (
                <span
                  style={{
                    fontSize: "9px",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    background: "rgba(34,197,94,0.2)",
                    color: "var(--brand)",
                    fontWeight: "700",
                  }}
                >
                  ADJUSTED
                </span>
              )}
            </div>

            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#fff",
                marginBottom: "20px",
                lineHeight: "1.4",
              }}
            >
              "{currentWeekData.plan.adjustment}"
            </div>

            {/* Task List */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              {currentWeekData.plan.tasks.map((task, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "13px",
                    color:
                      task.status === "done" ? "var(--brand)" : "var(--muted)",
                    opacity: task.status === "locked" ? 0.5 : 1,
                  }}
                >
                  {task.status === "done" ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        border: "1px solid var(--line)",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                  {task.title}
                </div>
              ))}
            </div>

            <button
              className="btn primary"
              onClick={handleCtaClick}
              style={{
                marginTop: "auto",
                width: "100%",
                justifyContent: "center",
              }}
            >
              {cta.label} <ArrowRight size={16} />
            </button>
            <div
              style={{
                textAlign: "center",
                marginTop: "12px",
                fontSize: "11px",
                color: "var(--muted)",
                opacity: 0.8,
              }}
            >
              {cta.helper}
            </div>
          </div>
        </div>
      </div>

      {/* WEEK DETAIL MODAL */}
      <WeekModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={modalData}
        mode={cta.mode}
      />
    </div>
  );
}
